import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import ErrorBoundary from '@justgiving/error-boundary';

import AboutCampaignAndCharitySection from '../../components/AboutCampaignAndCharitySection/AboutCampaignAndCharitySection';
import AboutCauseMini from '../../components/AboutCauseMini/AboutCauseMini';
import AboutTeam from '../../components/AboutTeam/AboutTeam';
import TeamAdminTabs from '../../components/AdminTabs/TeamAdminTabs';
import { Banner } from '../../components/Banner/Banner';
import DonationSummary, {
  DonationField,
} from '../../components/DonationSummary/DonationSummary';
import EventDetails from '../../components/EventDetails/EventDetails';
import FeatureImage from '../../components/FeatureImage/FeatureImage';
import TeamsHelmet from '../../components/Helmet/TeamsHelmet';
import IsPageOwner from '../../components/IsPageOwner/IsPageOwner';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import JoinTeam from '../../components/JoinTeam/JoinTeam';
import TeamLayout from '../../components/Layout/TeamLayout';
import MembersList from '../../components/MembersList/MembersList';
import MembersMini from '../../components/MembersMini/MembersMini';
import Page from '../../components/Page/Page';
import PageSection from '../../components/PageSection/PageSection';
import QRModal from '../../components/QRModal/QRModal';
import SocialButtonsList from '../../components/SocialButtons/SocialButtonsList';
import { TeamsShareProvider } from '../../components/SocialButtons/TeamsShareProvider';
import Story from '../../components/Story/Story';
import SupportersList from '../../components/SupportersList/SupportersList';
import TeamCTA from '../../components/TeamCTA/TeamCTA';
import TotaliserWrapper from '../../components/TotaliserWrapper/TotaliserWrapper';
import { Totals } from '../../components/Totals/Totals';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { firstArrayItem } from '../../utils/firstArrayItem';
import { filterByTypename } from '../../utils/graphql';

import '../Pages.scss';

const TeamsPage: FunctionComponent = () => {
  const basePageData = useBasePageQuery();
  const data = basePageData.data?.page;
  const { t } = useTranslation('common', { useSuspense: false });

  const title = data?.title;
  const beneficiaries = data?.relationships?.beneficiaries;
  const charities = filterByTypename(beneficiaries?.nodes ?? [], 'Charity');

  const firstCharity = firstArrayItem(charities);

  const membersListRef = React.createRef<HTMLElement>();

  return (
    <TeamsShareProvider>
      <Page>
        <IsQRCode>
          <QRModal ownerName={title} />
        </IsQRCode>
        <TeamsHelmet />
        <Banner />
        <JoinTeam />
        <IsPageOwner>
          <TeamAdminTabs />
        </IsPageOwner>
        <TeamLayout
          slots={{
            featureImage: <FeatureImage />,
            membersMini: <MembersMini />,
            title: <>{title}</>,
            totals: (
              <>
                <TotaliserWrapper />
                <Totals />
              </>
            ),
            callsToAction: <TeamCTA membersListRef={membersListRef} />,
            titleSupplementary: (
              <>
                <p className="jg-text--light jg-pages-teams-charitysummary jg-space-mbn">
                  <Trans
                    i18nKey="team.fundraisingFor"
                    count={(beneficiaries && beneficiaries.totalCount) || 0}
                  >
                    Team fundraising for{' '}
                    <a
                      href={`${firstCharity && firstCharity.profilePageUrl}`}
                      className="jg-text--regular"
                    >
                      {{ charityName: firstCharity && firstCharity.name }}
                    </a>
                  </Trans>
                </p>
                <ErrorBoundary fallbackContent={<></>}>
                  <EventDetails />
                </ErrorBoundary>
              </>
            ),
            causeMini: <AboutCauseMini />,
            story: (
              <PageSection
                title={t('story.title')}
                className="jg-pages-teams-story"
              >
                <Story>
                  <p>{t('team.defaultStory.part1')}</p>
                  <p>{t('team.defaultStory.part2')}</p>
                  <p>{t('team.defaultStory.part3')}</p>
                </Story>

                <h2 className="jg-text--center jg-h3">
                  <Trans i18nKey="story.shareTitle">Share this story</Trans>
                </h2>
                <SocialButtonsList />
              </PageSection>
            ),
            main: <MembersList listRef={membersListRef} />,
            aside: (
              <SupportersList
                noSupportersMessage={t(
                  'team.noSupportersMessage',
                  'Try making a donation to a team member.',
                )}
              />
            ),
            aboutTeam: (
              <PageSection
                title={t('page.aboutTheTeam')}
                hasBorder
                titleClass="jg-h5 jg-space-mbmd"
              >
                <AboutTeam />
              </PageSection>
            ),
            contentFooterPrimary: (
              <AboutCampaignAndCharitySection beneficiariesLimit={24} />
            ),
            contentFooterSecondary: (
              <DonationSummary
                fieldsToShow={[
                  DonationField.OnlineAmount,
                  DonationField.OfflineAmount,
                ]}
              />
            ),
          }}
        />
        <script
          src="//rum-static.pingdom.net/pa-5d2710d114cfaa00080000fc.js"
          async
        ></script>
      </Page>
    </TeamsShareProvider>
  );
};

export default TeamsPage;
